import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  X,
  ChevronRight,
  ChevronDown,
  CreditCard,
  Plus,
  Clock,
  Settings,
  BadgePercent,
  BarChart2,
  FileText,
} from "lucide-react";
import usePremiumStatus from "../../hooks/usePremiumStatus";
const logo = new URL(`../../assets/images/logo.png`, import.meta.url);

const BusinessSidebar = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const { isPremium } = usePremiumStatus();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if any child item is active to determine if parent should be expanded
  const isChildActive = (children) => {
    if (!children) return false;
    return children.some((child) => {
      if (child.href === location.pathname) return true;
      if (child.children) return isChildActive(child.children);
      return false;
    });
  };

  const navigationItems = [
    { id: "dashboard", label: "Dashboard", icon: Home, href: "/business/dashboard" },
    // Only show Transactions tab for Premium users
    ...(isPremium ? [{
      id: "transactions",
      label: "Transactions",
      icon: CreditCard,
      children: [
        { id: "new-transaction", label: "New Transaction", icon: Plus, href: "/business/transactions" },
        { id: "transaction-history", label: "Transaction History", icon: Clock, href: "/business/transactions/history" },
      ],
    }] : []),
    { id: "discount", label: "Discount", icon: BadgePercent, href: "/business/discount" },
    { id: "reports", label: "Reports", icon: BarChart2, href: "/business/reports" },
    ...(isPremium ? [{ id: "invoices", label: "Invoices", icon: FileText, href: "/business/invoices" }] : []),
    { id: "settings", label: "Profile", icon: Settings, href: "/business/settings" },
  ];

  // Initialize expanded items based on active child items
  const getInitialExpandedItems = () => {
    const expanded = [];
    const checkAndExpand = (items, parentId = null) => {
      items.forEach((item) => {
        if (item.children) {
          if (isChildActive(item.children)) {
            expanded.push(item.id);
            if (parentId) expanded.push(parentId);
          }
          checkAndExpand(item.children, item.id);
        }
      });
    };
    checkAndExpand(navigationItems);
    return expanded;
  };

  const [expandedItems, setExpandedItems] = useState(() => getInitialExpandedItems());

  // Update expanded items when location changes
  useEffect(() => {
    setExpandedItems(getInitialExpandedItems());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleItemClick = (item) => {
    // If item has children, toggle expansion
    if (item.children) {
      toggleExpanded(item.id);
    }
    // If item has href, navigate to it
    if (item.href) {
      navigate(item.href);
      // Close sidebar on mobile after navigation
      if (onToggle && !isDesktop) {
        onToggle();
      }
    }
  };

  const handleChildClick = (child) => {
    if (child.href) {
      navigate(child.href);
      // Close sidebar on mobile after navigation
      if (onToggle && !isDesktop) {
        onToggle();
      }
    }
  };

  return (
    <>
      {/* Sidebar */}
      <aside
        className={`theme-sidebar fixed lg:static inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo/Brand */}
          <div className="flex items-center justify-between h-[85px] px-6 border-b border-sidebar-border">
            <div
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => navigate("/business/dashboard")}
            >
              <img src={logo} alt="Logo" className="w-32" />
            </div>
            <button
              onClick={onToggle}
              className="lg:hidden p-2 rounded-md bg-card-bg border border-card-border text-text-primary hover:bg-surface-50 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto scrollbar-hide">
            <ul className="space-y-2">
              {navigationItems.map((item) => {
                const IconComponent = item.icon;
                const isActive = location.pathname === item.href;
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedItems.includes(item.id);
                const hasActiveChild = hasChildren && isChildActive(item.children);

                return (
                  <li key={item.id}>
                    <button
                      onClick={() => handleItemClick(item)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-left rounded-lg transition-colors duration-200 ${
                        (isActive && !hasChildren) || hasActiveChild
                          ? "theme-sidebar-active"
                          : "text-text-secondary hover:bg-surface-50 hover:text-text-primary"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <IconComponent
                          className={`h-5 w-5 ${
                            (isActive && !hasChildren) || hasActiveChild
                              ? "text-white"
                              : "text-text-tertiary"
                          }`}
                        />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {hasChildren ? (
                          <ChevronDown
                            className={`h-4 w-4 text-text-tertiary transform transition-transform duration-200 ${
                              isExpanded ? "rotate-180" : ""
                            } ${
                              hasActiveChild ? "text-white" : ""
                            }`}
                          />
                        ) : (
                          <ChevronRight
                            className={`h-4 w-4 text-text-tertiary ${
                              isActive ? "text-white" : ""
                            }`}
                          />
                        )}
                      </div>
                    </button>

                    {/* Submenu */}
                    {hasChildren && (
                      <div
                        className={`ml-4 mt-2 overflow-hidden transition-all duration-200 ${
                          isExpanded
                            ? "max-h-96 opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <ul className="space-y-1 border-l-2 border-border-light p-2">
                          {item.children.map((child) => {
                            const ChildIconComponent = child.icon;
                            const isChildActive = location.pathname === child.href;

                            return (
                              <li key={child.id} className="m-2">
                                <button
                                  onClick={() => handleChildClick(child)}
                                  className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded-md transition-colors duration-200 ${
                                    isChildActive
                                      ? "theme-sidebar-active"
                                      : "text-text-secondary hover:bg-surface-50 hover:text-text-primary"
                                  }`}
                                >
                                  <ChildIconComponent
                                    className={`h-4 w-4 ${
                                      isChildActive
                                        ? "text-white"
                                        : "text-text-tertiary"
                                    }`}
                                  />
                                  <span className="text-sm font-medium">
                                    {child.label}
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-sidebar-border">
            <div className="text-xs text-text-tertiary text-center">
              © 2025 Connectri
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={onToggle}
          />
        </>
      )}
    </>
  );
};

export default BusinessSidebar;
